import React, { useCallback, useState } from "react";
import ResumeSubscriptionButtonContainer from "../subscriptions/ResumeSubscriptionButtonContainer";
import { css } from "glamor";
import { FaChevronDown } from "react-icons/fa";
import { CircleX, CreditCard, MoreVertical } from "lucide-react";
import Modal from "../library/Modal";
import formatDate from "../../utils/formatDate";
import { toBRLCurrency } from "../../utils/formatCurrency";
import CreditCardIcon from "./CreditCardIcon";
import FailedChargeBanner from "./FailedChargeBanner";
import PaymentHistory from "./PaymentHistory";
import {
  statusVariant,
  statusText,
  subscriptionTitle,
  subscriptionPrice,
  nextChargeDate,
  paymentMethodLabel,
  billingDateLabel,
  renewalOrEndDateText,
  isFutureDate,
  formatOptionalDate,
  hasPaymentAlert,
  purchaseStatusVariant,
  getOverdueDays,
  getChargeFailureReason,
  getLastChargeStatusText,
} from "./financeSubscriptionHelpers";

// ─── Styles ──────────────────────────────────────────────────────────────────

const cardClass = css({
  position: "relative",
  minWidth: 0,
  background: "#FFFFFF",
  border: "0.6px solid #E2E8F0",
  borderRadius: 10,
  padding: "14px 14px 12px",
  transition: "border-color 0.18s",
  "@media(max-width: 640px)": {
    padding: 12,
  },
});

const cardHeaderClass = css({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 10,
  minWidth: 0,
});

const titleRowClass = css({
  display: "flex",
  alignItems: "center",
  gap: 7,
  flexWrap: "wrap",
  minWidth: 0,
});

const titleClass = css({
  minWidth: 0,
  color: "#0F172A",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "20px",
  overflowWrap: "anywhere",
});

const statusClass = (variant) => css({
  display: "inline-flex",
  alignItems: "center",
  height: 20,
  padding: "0 7px",
  borderRadius: 4,
  border: `0.6px solid ${variant.border}`,
  background: variant.background,
  color: variant.color,
  fontSize: 10,
  fontWeight: 500,
  lineHeight: "16px",
  whiteSpace: "nowrap",
});

const iconButtonClass = css({
  flexShrink: 0,
  width: 28,
  height: 28,
  border: "none",
  borderRadius: 6,
  background: "transparent",
  color: "#64748B",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  ":hover": {
    background: "#F1F5F9",
  },
});

const menuIconButtonClass = css(iconButtonClass, {
  width: 44,
  height: 32,
  padding: "0 20px 0 8px",
  color: "#64748B",
  fontSize: 16,
  ":hover": {
    background: "transparent",
    color: "#64748B",
  },
});

const cardHeaderActionsClass = css({
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  flexShrink: 0,
});

const chevronClass = (expanded) => css({
  transition: "transform 180ms ease",
  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
});

const metaClass = css({
  display: "flex",
  alignItems: "center",
  gap: 6,
  flexWrap: "wrap",
  minWidth: 0,
  marginTop: 8,
  color: "#64748B",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "18px",
  "> span": {
    minWidth: 0,
    overflowWrap: "anywhere",
  },
});

const cycleBadgeClass = css({
  display: "inline-flex",
  alignItems: "center",
  height: 18,
  padding: "0 6px",
  borderRadius: 4,
  background: "#F0F9FF",
  color: "#0369A1",
  fontSize: 11,
  fontWeight: 500,
});

const footClass = css({
  display: "flex",
  alignItems: "center",
  gap: 6,
  minWidth: 0,
  marginTop: 10,
  paddingTop: 10,
  borderTop: "0.6px solid #F1F5F9",
  color: "#94A3B8",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "18px",
  "> span": {
    minWidth: 0,
    overflowWrap: "anywhere",
  },
});

const detailsClass = css({
  marginTop: 14,
  paddingTop: 14,
  borderTop: "0.6px solid #F1F5F9",
});

const detailsGridClass = css({
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "14px 16px",
  "@media(max-width: 760px)": {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
  "@media(max-width: 460px)": {
    gridTemplateColumns: "1fr",
    gap: 10,
  },
});

const detailFieldClass = css({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
});

const detailLabelClass = css({
  color: "#94A3B8",
  fontSize: 11,
  fontWeight: 400,
  lineHeight: "16px",
});

const detailValueClass = css({
  color: "#0F172A",
  fontSize: 12,
  fontWeight: 500,
  lineHeight: "18px",
  wordBreak: "break-word",
});

const detailValueSuccessClass = css(detailValueClass, { color: "#379C5C" });
const detailValueWarningClass = css(detailValueClass, { color: "#C2410C" });
const detailValueDangerClass = css(detailValueClass, { color: "#C56363" });

const detailButtonRowClass = css({
  display: "flex",
  alignItems: "center",
  width: "100%",
  marginTop: 16,
  "@media(max-width: 460px)": {
    alignItems: "stretch",
  },
});

const changePaymentButtonClass = css({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 32,
  padding: "0 12px",
  borderRadius: 6,
  border: "0.6px solid #E2E8F0",
  background: "#FFFFFF",
  color: "#475569",
  fontSize: 12,
  fontWeight: 500,
  lineHeight: "18px",
  cursor: "pointer",
  ":hover": {
    borderColor: "#CBD5E1",
    background: "#F8FAFC",
    color: "#0F172A",
  },
  "@media(max-width: 460px)": {
    width: "100%",
  },
});


const historyActionClass = css({
  marginTop: 14,
});

const historyButtonClass = css({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: 0,
  border: "none",
  background: "transparent",
  color: "#475569",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: "18px",
  cursor: "pointer",
  ":hover": {
    color: "#0F172A",
  },
});

const historyChevronClass = (expanded) => css({
  transition: "transform 180ms ease",
  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
});

const subscriptionActionsListClass = css({
  position: "absolute",
  top: 51,
  right: 14,
  zIndex: 20,
  display: "flex",
  flexDirection: "column",
  width: 182,
  minHeight: 72,
  overflow: "hidden",
  background: "#FFFFFF",
  border: "0.612036px solid #E2E8F0",
  borderRadius: 6,
  boxShadow: "0px 10px 18px rgba(15, 23, 42, 0.12)",
  "@media(max-width: 460px)": {
    right: 12,
  },
});

const subscriptionActionButtonClass = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 9,
  width: "100%",
  minHeight: 36,
  padding: "0 12px",
  border: "none",
  background: "#FFFFFF",
  color: "#0F172A",
  fontFamily: "Montserrat, sans-serif",
  fontSize: 12,
  fontWeight: 500,
  lineHeight: "18px",
  textAlign: "left",
  cursor: "pointer",
  ":hover": {
    background: "#F8FAFC",
  },
  ":disabled": {
    color: "#94A3B8",
    cursor: "not-allowed",
  },
});

const subscriptionActionDangerButtonClass = css(subscriptionActionButtonClass, {
  minHeight: 36,
  borderTop: "0.612036px solid #F1F5F9",
  color: "#EF4444",
});

const subscriptionActionIconClass = css({
  width: 13,
  height: 13,
  minWidth: 13,
  color: "currentColor",
});

const subscriptionCancelOptionsClass = css({
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

const subscriptionCancelOptionClass = (selected) => css({
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  padding: 12,
  border: `0.612036px solid ${selected ? "#0369A1" : "#E2E8F0"}`,
  borderRadius: 8,
  background: selected ? "#F0F9FF" : "#FFFFFF",
  cursor: "pointer",
});

const subscriptionCancelSectionLabelClass = css({
  fontFamily: "Montserrat, sans-serif",
  color: "#0F172A",
  fontSize: 12,
  fontWeight: 500,
  lineHeight: "18px",
  margin: "0 0 8px",
});

const subscriptionCancelOptionTextClass = css({
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

const subscriptionCancelOptionTitleClass = css({
  fontFamily: "Montserrat, sans-serif",
  color: "#0F172A",
  fontSize: 12,
  fontWeight: 500,
  lineHeight: "18px",
});

const subscriptionCancelOptionSubtitleClass = css({
  fontFamily: "Montserrat, sans-serif",
  color: "#64748B",
  fontSize: 11,
  fontWeight: 400,
  lineHeight: "16.5px",
});

const subscriptionCancelRadioClass = css({
  marginTop: 3,
  accentColor: "#0369A1",
  flexShrink: 0,
});

const deleteModalClass = css({
  "&.ui.tiny.modal": {
    maxWidth: 420,
    borderRadius: 14,
    overflow: "hidden",
    padding: 0,
    background: "#FFFFFF",
    fontFamily: "Montserrat, sans-serif",
  },
});

const deleteModalInnerClass = css({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  padding: "24px 24px 0",
  background: "#FFFFFF",
});

const deleteModalActionsRowClass = css({
  display: "flex",
  gap: 8,
  paddingBottom: 20,
  "@media(max-width: 420px)": {
    flexDirection: "column-reverse",
  },
});

const deleteModalHeaderClass = css({
  display: "flex",
  alignItems: "center",
  gap: 10,
});

const deleteModalIconClass = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 36,
  borderRadius: "50%",
  background: "#FEF2F2",
  flexShrink: 0,
  color: "#EF4444",
});

const deleteModalTitleClass = css({
  fontFamily: "Montserrat, sans-serif",
  margin: 0,
  color: "#0F172A",
  fontSize: 15,
  fontWeight: 600,
  lineHeight: "22.5px",
});

const deleteModalDescriptionClass = css({
  fontFamily: "Montserrat, sans-serif",
  margin: 0,
  color: "#475569",
  fontSize: 13,
  fontWeight: 400,
  lineHeight: "22px",
});

const deleteModalCancelButtonClass = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  minHeight: 44,
  padding: "0 8px",
  borderRadius: 8,
  border: "0.612036px solid #E2E8F0",
  background: "#FFFFFF",
  color: "#0F172A",
  fontFamily: "Montserrat, sans-serif",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "21px",
  cursor: "pointer",
  ":hover": {
    background: "#F8FAFC",
  },
  ":disabled": {
    color: "#94A3B8",
    cursor: "not-allowed",
  },
  "@media(max-width: 420px)": {
    width: "100%",
  },
});

const deleteModalConfirmButtonClass = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  minHeight: 44,
  padding: "0 8px",
  borderRadius: 8,
  border: "none",
  background: "#0F172A",
  color: "#FFFFFF",
  fontFamily: "Montserrat, sans-serif",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "19.5px",
  cursor: "pointer",
  ":hover": {
    background: "#1E293B",
  },
  ":disabled": {
    background: "#94A3B8",
    cursor: "not-allowed",
  },
  "@media(max-width: 420px)": {
    width: "100%",
  },
});

// ─── Helpers internos ────────────────────────────────────────────────────────

function detailLastChargeClass(subscription) {
  if (subscription.status === "canceled") return detailValueDangerClass;
  if (subscription.canRetryCharge || subscription.failedCharge || subscription.pendingPixPayment) return detailValueWarningClass;
  if (subscription.lastChargeStatus === "paid" || subscription.lastChargeStatus === "sell") return detailValueSuccessClass;

  return detailValueClass;
}

// ─── Sub-componentes internos ─────────────────────────────────────────────────

function DetailField({ label, value, valueClassName = detailValueClass }) {
  return (
    <div className={detailFieldClass}>
      <span className={detailLabelClass}>{label}</span>
      <span className={valueClassName}>{value || "-"}</span>
    </div>
  );
}

function SubscriptionActionsMenu({ open, subscription, loading, onChangePayment, onCancel }) {
  if (!open || !subscription) return null;

  return (
    <div className={subscriptionActionsListClass}>
      <button
        type="button"
        className={subscriptionActionButtonClass}
        onClick={onChangePayment}
        disabled={loading}
      >
        <CreditCard className={`${subscriptionActionIconClass}`} strokeWidth={1.8} />
        Alterar pagamento
      </button>
      <button
        type="button"
        className={subscriptionActionDangerButtonClass}
        onClick={onCancel}
        disabled={loading || subscription.status !== "active"}
      >
        <CircleX className={`${subscriptionActionIconClass}`} strokeWidth={1.8} />
        Cancelar assinatura
      </button>
    </div>
  );
}

function CancelSubscriptionModal({ subscription, cancelOptions, loading, onClose, onConfirm }) {
  const [refund, setRefund] = useState(false);
  const canRefund = !!cancelOptions?.canRefundUntil;
  const accessEndDate = cancelOptions?.accessEndDate;
  const accessMessage = isFutureDate(accessEndDate) && !canRefund
    ? ` Você continuará com acesso ao conteúdo até ${formatOptionalDate(accessEndDate)}.`
    : "";

  if (!subscription) return null;

  return (
    <Modal open={!!subscription} onClose={loading ? undefined : onClose} className={deleteModalClass} size="tiny">
      <div className={deleteModalInnerClass}>
        <div className={deleteModalHeaderClass}>
          <div className={deleteModalIconClass}>
            <CircleX size={18} strokeWidth={1.8} />
          </div>
          <h2 className={deleteModalTitleClass}>Cancelar assinatura</h2>
        </div>

        <p className={deleteModalDescriptionClass}>
          Tem certeza que deseja cancelar{" "}
          <strong>{subscriptionTitle(subscription)}</strong>?
          {accessMessage}
        </p>

        {canRefund && (
          <>
            <p className={subscriptionCancelSectionLabelClass}>Deseja solicitar reembolso?</p>
            <div className={subscriptionCancelOptionsClass}>
              <label className={subscriptionCancelOptionClass(!refund)}>
                <input
                  type="radio"
                  name="refundLastPayment"
                  checked={!refund}
                  onChange={() => setRefund(false)}
                  className={subscriptionCancelRadioClass}
                  disabled={loading}
                />
                <div className={subscriptionCancelOptionTextClass}>
                  <span className={subscriptionCancelOptionTitleClass}>
                    {isFutureDate(accessEndDate)
                      ? `Encerrar assinatura e manter acesso até ${formatOptionalDate(accessEndDate)}`
                      : "Encerrar assinatura"}
                  </span>
                  {isFutureDate(accessEndDate) && (
                    <span className={subscriptionCancelOptionSubtitleClass}>
                      Você poderá usar o conteúdo até o final do ciclo atual.
                    </span>
                  )}
                </div>
              </label>
              <label className={subscriptionCancelOptionClass(refund)}>
                <input
                  type="radio"
                  name="refundLastPayment"
                  checked={refund}
                  onChange={() => setRefund(true)}
                  className={subscriptionCancelRadioClass}
                  disabled={loading}
                />
                <div className={subscriptionCancelOptionTextClass}>
                  <span className={subscriptionCancelOptionTitleClass}>
                    Encerrar assinatura e solicitar reembolso
                  </span>
                  <span className={subscriptionCancelOptionSubtitleClass}>
                    Você perderá acesso aos conteúdos imediatamente.
                  </span>
                </div>
              </label>
            </div>
          </>
        )}

        <div className={deleteModalActionsRowClass}>
          <button
            type="button"
            className={deleteModalCancelButtonClass}
            onClick={onClose}
            disabled={loading}
          >
            Manter
          </button>
          <button
            type="button"
            className={deleteModalConfirmButtonClass}
            onClick={() => onConfirm(refund)}
            disabled={loading || subscription.status !== "active"}
          >
            {loading ? "Cancelando..." : "Confirmar cancelamento"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function SubscriptionCard({
  subscription,
  invoices,
  cancelOptions,
  actionLoading,
  onChangePayment,
  onCancelSubscription,
  onRetryCharge,
  purchase = null,
  cycleNumber = null,
  isLatest = true,
  historyPurchases = undefined,
}) {
  const [expanded, setExpanded] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const renewalDate = isLatest ? nextChargeDate(subscription) : null;
  const price = purchase?.productPrice || subscriptionPrice(subscription);
  const overdueDays = getOverdueDays(subscription.lastChargeDueAt);
  const chargeFailureReason = getChargeFailureReason(subscription);
  const displayCycleNumber = cycleNumber ?? Math.max(
    subscription.purchases?.length || 0,
    subscription.cycleCount || 0,
  );
  const historyList = historyPurchases !== undefined ? historyPurchases : (subscription.purchases || []);
  const cardStatusVariant = (!isLatest && purchase) ? purchaseStatusVariant(purchase) : statusVariant(subscription);
  const cardStatusText = (!isLatest && purchase) ? purchaseStatusVariant(purchase).text : statusText(subscription);

  const handleChangePayment = useCallback(() => {
    setActionsOpen(false);
    onChangePayment(subscription);
  }, [onChangePayment, subscription]);
  const handleToggleActions = useCallback(() => {
    setActionsOpen((current) => !current);
  }, []);
  const handleOpenCancelModal = useCallback(() => {
    setActionsOpen(false);
    setCancelModalOpen(true);
  }, []);
  const handleCloseCancelModal = useCallback(() => setCancelModalOpen(false), []);
  const handleConfirmCancel = useCallback((refundLastPayment) => (
    onCancelSubscription(subscription.id, refundLastPayment).then(() => {
      setCancelModalOpen(false);
    })
  ), [onCancelSubscription, subscription.id]);

  return (
    <article className={cardClass}>
      <div className={cardHeaderClass}>
        <div className={titleRowClass}>
          <span className={titleClass}>{subscriptionTitle(subscription)}</span>
          <span className={statusClass(cardStatusVariant)}>{cardStatusText}</span>
        </div>

        <div className={cardHeaderActionsClass}>
          <button
            type="button"
            className={iconButtonClass}
            onClick={() => setExpanded((current) => !current)}
            aria-expanded={expanded}
            aria-label={expanded ? "Ocultar detalhes" : "Ver detalhes"}
          >
            <FaChevronDown className={chevronClass(expanded)} />
          </button>
          {isLatest && (
            <button
              type="button"
              className={menuIconButtonClass}
              onClick={handleToggleActions}
              aria-expanded={actionsOpen}
              aria-label="Gerenciar assinatura"
            >
              <MoreVertical size={16} strokeWidth={2} />
            </button>
          )}
        </div>
      </div>

      <div className={metaClass}>
        {price && <span>{toBRLCurrency(price)}/mês</span>}
        <span className={cycleBadgeClass}>Ciclo {displayCycleNumber}</span>
        {isLatest && renewalDate && (
          <span>Próx. cobrança: {formatDate(renewalDate, "DD/MM/YYYY")}</span>
        )}
        {isLatest && !renewalDate && subscription.endDate && (
          <span>Término: {formatDate(subscription.endDate, "DD/MM/YYYY")}</span>
        )}
        {!isLatest && purchase?.createdAt && (
          <span>{formatDate(purchase.createdAt, "DD/MM/YYYY")}</span>
        )}
      </div>

      <div className={footClass}>
        <CreditCardIcon />
        <span>{paymentMethodLabel(subscription)}</span>
      </div>

      {expanded && (
        <div className={detailsClass}>
          {isLatest && hasPaymentAlert(subscription) && (
            <FailedChargeBanner
              subscription={subscription}
              overdueDays={overdueDays}
              chargeFailureReason={chargeFailureReason}
              price={price}
              onRetryClick={onRetryCharge}
            />
          )}

          <div className={detailsGridClass}>
            <DetailField label="ID da assinatura" value={subscription.id} />
            <DetailField label="Ciclo" value={`Ciclo ${displayCycleNumber}`} />
            <DetailField label="Frequência" value="Mensal (a cada 30 dias)" />
            <DetailField
              label="Início"
              value={subscription.startDate ? formatDate(subscription.startDate, "DD/MM/YYYY") : "-"}
            />
            <DetailField
              label={billingDateLabel(subscription)}
              value={renewalOrEndDateText(subscription, renewalDate)}
            />
            <DetailField
              label="Última cobrança"
              value={getLastChargeStatusText(subscription.lastChargeStatus) || "-"}
              valueClassName={detailLastChargeClass(subscription)}
            />
            <DetailField label="Método" value={paymentMethodLabel(subscription)} />
          </div>

          {isLatest && (
            <div className={detailButtonRowClass}>
              {(subscription.status === "canceled" || subscription.status === "pending") ? (
                <ResumeSubscriptionButtonContainer subscription={subscription} />
              ) : (
                <button
                  type="button"
                  className={changePaymentButtonClass}
                  onClick={handleChangePayment}
                >
                  Alterar pagamento
                </button>
              )}
            </div>
          )}

          {historyList.length > 0 && (
            <div className={historyActionClass}>
              <button
                type="button"
                className={historyButtonClass}
                onClick={() => setHistoryExpanded((current) => !current)}
                aria-expanded={historyExpanded}
              >
                <FaChevronDown size={12} className={historyChevronClass(historyExpanded)} />
                Histórico de pagamentos ({historyList.length})
              </button>
              {historyExpanded && (
                <PaymentHistory purchases={historyList} invoices={invoices} />
              )}
            </div>
          )}
        </div>
      )}

      {isLatest && (
        <SubscriptionActionsMenu
          open={actionsOpen}
          subscription={subscription}
          loading={actionLoading}
          onChangePayment={handleChangePayment}
          onCancel={handleOpenCancelModal}
        />
      )}
      {isLatest && (
        <CancelSubscriptionModal
          subscription={cancelModalOpen ? subscription : null}
          cancelOptions={cancelOptions}
          loading={actionLoading}
          onClose={handleCloseCancelModal}
          onConfirm={handleConfirmCancel}
        />
      )}
    </article>
  );
}
